<template>
  <Loader :value="promises.fetchOne" :message="$t('loading', { resource: null }) + '...'" class="contract">
    <div class="container view">
      <HeaderComponent :breadcrumb="breadcrumb">
        <template #title>
          <div class="row middle">
            <h1 class="header__title">{{ $metaInfo.title }}</h1>
            <div class="ml-2">
              <Badge
                v-if="item.status === 'accepted'"
                theme="success"
                icon="check-circle"
                :value="$tc('contractStatusAccepted')"
              />
              <Badge v-if="item.status === 'declined'" theme="error" :value="$tc('contractStatusDeclined')" />
            </div>
          </div>
        </template>
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="contract" :query="{ contract: id }">
              <template #bottom>
                <ButtonComponent
                  v-if="hasSigningAuthority && item.status === 'accepted'"
                  :label="$t('cancelContract')"
                  @click="state = 'cancelContract'"
                  :icon="['fal', 'times']"
                />
              </template>
            </Actions>
          </div>
        </template>
      </HeaderComponent>
      <div class="contract__content">
        <component
          v-if="item.id"
          :is="stateComponent"
          :value="item"
          :has-access="hasSigningAuthority"
          @accept="state = 'accept'"
          @decline="state = 'decline'"
          @accepted="onAccepted"
          @declined="onDeclined"
          @cancel="state = null"
        />
        <div class="contract__sidebar">
          <ResidentialCard v-if="$path('reference.onModel', item) === 'Residential'" :value="item.reference" />
          <UserCard
            v-for="signer in signers"
            :key="signer.email"
            :value="signer"
            :avatar="`/api/avatar/${getInitials(signer.name)}`"
          >
            <template #subtitle>
              <div class="col">
                <a :href="`mailto:${signer.email}`">{{ signer.email }}</a>
                <a :href="`tel:${signer.phone}`" style="margin-top: 10px">{{ signer.phone }}</a>
              </div>
            </template>
          </UserCard>
        </div>
      </div>
    </div>
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Initials } from '@/utils'

import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Badge from '@/components/Badge'
import States from './States'
import ResidentialCard from '@/components/Residential/Card'
import UserCard from '@/components/User/Card'
import Actions from '@/components/Actions'
import { jwtDecode } from 'jwt-decode'

export default {
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
  },
  data() {
    return {
      state: null,
    }
  },
  computed: {
    ...mapState('Contract', ['item', 'promises']),
    stateComponent() {
      switch (this.state) {
        case 'accept':
          return States.Accept
        case 'decline':
          return States.Decline
        case 'cancelContract':
          return States.Void
        default:
          return States.Default
      }
    },
    hasSigningAuthority() {
      switch (this.$path('item.type')) {
        case 'shop':
        case 'shop-partial':
          return this.$store.getters['Project/hasRole']('shop.signingAuthority', this.$path('item.reference.project'))
        case 'custom':
          return (
            this.$store.getters.isAdmin ||
            this.$store.getters.hasAccess('contacts') ||
            this.item.signingAuthority.some(s => s.id === this.$store.state.user.id)
          )
        default:
          return this.$store.getters['Project/hasRole']('signingAuthority', this.$path('item.reference.project'))
      }
    },
    signers() {
      return this.item.signers || []
    },
    breadcrumb() {
      if (!this.item.id) return []

      switch (this.item.reference.onModel) {
        case 'Residential':
          return [
            { title: this.$tc('project', 2), action: { name: 'projects' } },
            {
              title: this.$path('item.reference.project.name'),
              action: { name: 'project-dashboard', params: { id: this.$path('item.reference.project.id') } },
            },
            { title: this.$path('item.reference.name') },
            { title: this.$t(this.item.type + 'Contract') },
          ]
        case 'Contact':
          return [
            {
              title: this.item.reference.name,
              action: { name: 'contact-dashboard', params: { id: this.item.reference.id } },
            },
            { title: this.$tc('document', 2) },
            { title: this.$metaInfo.title },
          ]
      }
    },
  },
  methods: {
    ...mapActions('Contract', {
      fetch(dispatch) {
        return dispatch('fetch', { id: this.id }).then(() => {
          if (!this.item.id) return this.$router.push({ name: 'notfound' })

          if (!this.$route.query['idfy-jwt']) return

          try {
            const token = jwtDecode(this.$route.query['idfy-jwt'])
            this.$router.replace({ query: { ...this.$route.query, ['idfy-jwt']: undefined } })
            if (token.SignSuccess) return this.onAccepted()
          } catch (error) {
            console.log(error)
          }
        })
      },
    }),
    getInitials: Initials,
    onAccepted() {
      this.item.status = 'accepted'
      this.state = null
    },
    onDeclined() {
      this.item.status = 'declined'
      this.state = null
    },
  },
  metaInfo() {
    return {
      title: this.item.title || this.$t('loading', { resource: null }) + '...',
    }
  },
  mounted() {
    if (this.$route.query.state) this.state = this.$route.query.state
  },
  components: {
    Card,
    HeaderComponent,
    UserCard,
    Badge,
    ResidentialCard,
    Actions,
  },
}
</script>

<style lang="scss">
.contract {
  background-color: $background-grey;

  &__preview {
    width: 100%;

    @include aspect-ratio(210, 257);

    .content {
      width: 100%;
      height: 100%;
    }
  }

  &__sign {
    width: 100%;
    min-height: 1000px;
  }

  &__content {
    display: grid;
    grid-template-columns: 1fr 350px;
    align-items: start;
    grid-gap: 2rem;
  }

  &__sidebar {
    display: grid;
    grid-gap: 2rem;
  }

  &__body {
    .card__footer-content {
      justify-content: center;
    }
  }
}
</style>
